import { Canvas, Color } from '@thive/canvas';
import { Group } from '@thive/fddm-types';
import { Vector2, Vector3 } from 'three';
import { t } from 'i18next';

import { theme } from '@/modules/common/theme';
import { logger } from '@common/logging/logger';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { areasToFlatCollection, isHighwayArea } from '@/modules/floorplanDesign/helpers';
import { Area } from '@/modules/floorplanDesign/types';
import {
  findSegmentContainingPoint,
  templatesToFlatCollection,
} from '@/modules/floorplanDesign/helpers/helpers';
import { isShuttleStorageRackingArea } from '@/modules/floorplanDesign/helpers/typeguards';
import { useCanvasStore } from '@/modules/canvas/store';
import { ICanvasService } from '@/modules/canvas/services/Canvas/ICanvasService';
import { CanvasServiceFactory } from '@/modules/canvas/services/Canvas/CanvasServiceFactory';
import { DIST_CON_LINE_STROKE_WIDTH } from '@/modules/canvas/constants/elements';
import { IFddmService } from '@/modules/canvas/services/Floorplan/IFddmService';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { AppServiceFactory } from '@/modules/app/service/AppServiceFactory';
import { useSnackbarStore } from '@/modules/snackbar/store/useSnackbarStore';

import { factory as connectionFactory } from '../factories/connection';
import {
  getValidDistconTargetAreaTypes,
  isDistconAllowedBetweenAreaTypes,
} from '../distantConnection';
import { ActiveDistconSource, useActiveDistconStore } from '../store/useActiveDistconStore';
import { IDistconService } from './IDistconService';

export class DistconServiceImpl implements IDistconService {
  private floorplanDesignStore: typeof useFloorplanDesignStore;
  private canvasStore: typeof useCanvasStore;
  private activeDistconStore: typeof useActiveDistconStore;
  private canvasSvc: ICanvasService;
  private fddmSvc: IFddmService;

  public constructor(
    floorplanDesignStore: typeof useFloorplanDesignStore,
    canvasStore: typeof useCanvasStore,
    activeDistconStore: typeof useActiveDistconStore,
  ) {
    this.floorplanDesignStore = floorplanDesignStore;
    this.canvasStore = canvasStore;
    this.activeDistconStore = activeDistconStore;
    this.canvasSvc = CanvasServiceFactory.getInstance().createCanvasService();
    this.fddmSvc = FddmServiceFactory.getInstance().createFddmService();
  }

  activateTool(): void {
    this.canvasSvc.enableTargeting(this.getConnectableAreas().map((area) => area.id));
    const appSvc = AppServiceFactory.getInstance().createAppService();
    appSvc.setCanvasOverlay({
      message: t<string>(
        'interface:canvas_overlay.drawing_distcon_source',
        'Select the starting area for the distant connection',
      ),
    });
  }

  cancelTool(): void {
    this.activeDistconStore.getState().reset();
    this.canvasSvc.disableTargeting();
    const appSvc = AppServiceFactory.getInstance().createAppService();
    appSvc.setCanvasOverlay(null);
  }

  startDistcon(area: Area, clickedPosition: Vector3): void {
    const canvas = this.getCanvas();
    const source = this.buildSource(area, clickedPosition.clone().divideScalar(1000));
    if (!source) return;

    const stickyLinePosition = new Vector3(
      source.originPosition.x,
      source.originPosition.y,
    ).multiplyScalar(1000);

    canvas.updateTool('Draw.LineSticky', {
      type: 'Arrow',
      pointA: stickyLinePosition,
      stroke: Color.fromHex(theme.palette.primary.main),
      strokeWidth: DIST_CON_LINE_STROKE_WIDTH,
    });

    this.activeDistconStore.getState().setSource(source);
    this.canvasSvc.enableTargeting(this.getValidTargetIds(area));

    const appSvc = AppServiceFactory.getInstance().createAppService();
    appSvc.setCanvasOverlay({
      message: t<string>(
        'interface:canvas_overlay.drawing_distcon_target',
        'Select the destination area for the distant connection',
      ),
    });
  }

  endDistcon(area: Area, clickedPosition: Vector3): void {
    const { source } = this.activeDistconStore.getState();
    if (!source) {
      logger.error('Distant connection source not initialized');
      return;
    }

    const { showSnackbar } = useSnackbarStore.getState();

    if (!isDistconAllowedBetweenAreaTypes(source.area.areaType, area.areaType)) {
      showSnackbar(
        t(
          'errors:connection.distant.invalid_target',
          'Invalid. Please select one of the highlighted areas.',
        ),
      );
      return;
    }

    const targetId = isHighwayArea(area)
      ? findSegmentContainingPoint(
          area.segments,
          new Vector2(clickedPosition.x, clickedPosition.y).divideScalar(1000),
        )?.id
      : area.id;

    if (!targetId) {
      logger.error(
        `Could not determine the clicked segment for highway: ${area.id} at: ${clickedPosition}`,
      );
      return;
    }

    if (targetId === source.id) {
      showSnackbar(
        t(
          'errors:connection.distant.same_target_as_source',
          'Can not select the same area as both the start and end of a distant connection.',
        ),
      );
      return;
    }

    // All distant connections use case 3 (NEAREST, both directions).
    const draft = connectionFactory.buildDistantConnection(source.id, targetId);

    const result = this.fddmSvc.addInterAreaConnections([draft]);
    if (!result) {
      showSnackbar(`A connection between these areas already exists`);
      return;
    }

    // Reset and re-activate so user can chain another distcon.
    this.activeDistconStore.getState().reset();
    const appSvc = AppServiceFactory.getInstance().createAppService();
    this.canvasSvc.setActiveTool('cursor');
    appSvc.activateTool('distcon');
  }

  //
  // Private methods
  //

  private getCanvas(): Canvas {
    const canvas = this.canvasStore.getState().instance;
    if (!canvas) throw new Error('Canvas instance not available.');
    return canvas;
  }

  private buildSource(area: Area, clickedPosition: Vector3): ActiveDistconSource | null {
    if (isHighwayArea(area)) {
      const segment = findSegmentContainingPoint(area.segments, clickedPosition);
      if (!segment) {
        logger.error(
          'Could not define which segment was clicked when creating a distant connection. Segments: ',
          area.segments,
          '. clicked position: ',
          clickedPosition,
        );
        return null;
      }
      return {
        id: segment.id,
        area,
        type: 'HIGHWAY',
        originPosition: {
          x: segment.start.x + (segment.end.x - segment.start.x) / 2,
          y: segment.start.y + (segment.end.y - segment.start.y) / 2,
        },
      };
    }

    return {
      id: area.id,
      area,
      type: area.areaType,
      originPosition: { x: area.rectangle.centerX, y: area.rectangle.centerY },
    };
  }

  private getValidTargetIds(sourceArea: Area): string[] {
    const validTargetTypes = getValidDistconTargetAreaTypes(sourceArea.areaType);

    return this.getConnectableAreas()
      .filter((area) => validTargetTypes.includes(area.areaType))
      .map((area) => area.id);
  }

  private getConnectableAreas(): Area[] {
    const state = this.floorplanDesignStore.getState();
    const allAreas = areasToFlatCollection(state.design.areas);
    const areaIds = allAreas.map((area) => area.id);
    const groups = [
      ...state.getGroupsByAreaIds(areaIds),
      ...templatesToFlatCollection(state.design.templates).map((template) => template.group),
    ];
    const nonConnectableAreaIds = new Set([
      ...this.flattenGroups(groups)
        .filter((group) => !group.canConnect)
        .flatMap((group) => this.getGroupAreaIds(group)),
      ...allAreas.filter((area) => isShuttleStorageRackingArea(area)).map((area) => area.id),
    ]);

    return allAreas.filter((area) => !nonConnectableAreaIds.has(area.id));
  }

  private flattenGroups(groups: Group[]): Group[] {
    return groups.flatMap((group) => [group, ...this.flattenGroups(group.nestedGroups)]);
  }

  private getGroupAreaIds(group: Group): string[] {
    return [
      ...group.areaIds,
      ...group.nestedGroups.flatMap((nestedGroup) => this.getGroupAreaIds(nestedGroup)),
    ];
  }
}
